/**
 * AI Assistant — who the launcher, the drawer and /view/assistant are shown to.
 *
 * Kept as a dependency-free helper (and not inline in the store) so the rule can be unit tested
 * and so there is exactly one place the SPA decides it. It mirrors the backend's
 * AssistantAudience + AssistantAvailability; the backend stays the authority, this only hides
 * what it would refuse. It is the assistant's OWN rule: every other AI feature keeps the
 * Staff / Admin Staff rule of `appStore.canUseAIFeatures` (the tenant Admin is admitted here
 * and not there).
 */
export type AssistantAudienceKind = 'Internal' | 'CaseManager' | 'Specialist'

/** The same codes the backend returns (capabilities `unavailableReason`, 403 `error.code`). */
export type AssistantUnavailableReason = 'FEATURE_DISABLED' | 'ROLE_NOT_ALLOWED'

export interface AssistantAccessInput {
  /** Role names of the signed-in user, as the backend names them. */
  roles: string[] | null | undefined
  /** Tenant switches: AI Service, AI Assistant, and one per external audience. */
  features: {
    AIService?: boolean
    AIAssistant?: boolean
    AIAssistantCaseManager?: boolean
    AIAssistantSpecialist?: boolean
  }
}

/**
 * The decision for every backend role (null = not admitted) — the same table as
 * AssistantAudience.RoleDecisions. The tenant ADMIN is an internal user like Staff and Admin
 * Staff. SYSTEM_ADMIN, INSURER, CLIENT, INTERPRETER, COMPANY and PMS are names no account holds:
 * like the backend, this table has no decision for them and they are ignored.
 */
const ROLE_DECISIONS: Record<string, AssistantAudienceKind | null> = {
  STAFF: 'Internal',
  ADMIN_STAFF: 'Internal',
  ADMIN: 'Internal',
  CASE_MANAGER: 'CaseManager',
  SPECIALIST: 'Specialist',
  ACCOUTING: null,
  CONTRACTOR: null,
}

/**
 * The audience of a user by role, or null when the assistant is not for them. The strictest
 * decision among the roles wins: a role that is not admitted blocks the user, an external role
 * outranks an internal one, and two different external roles are not admitted. A role without a
 * decision is ignored.
 */
export const resolveAssistantAudience = (
  roles: string[] | null | undefined
): AssistantAudienceKind | null => {
  let audience: AssistantAudienceKind | null = null

  for (const role of roles ?? []) {
    if (!Object.prototype.hasOwnProperty.call(ROLE_DECISIONS, role)) {
      continue
    }
    const decision = ROLE_DECISIONS[role]
    if (decision === null) {
      return null
    }
    if (audience === null || audience === 'Internal') {
      audience = decision
    } else if (decision !== 'Internal' && decision !== audience) {
      return null
    }
  }

  return audience
}

/**
 * Why the assistant is not available to this user, or null when it is. Checked in the backend's
 * order: the tenant switches first, then the role and the switch of its audience.
 */
export const getAssistantUnavailableReason = ({
  roles,
  features,
}: AssistantAccessInput): AssistantUnavailableReason | null => {
  if (!features.AIService || !features.AIAssistant) {
    return 'FEATURE_DISABLED'
  }

  switch (resolveAssistantAudience(roles)) {
    case 'Internal':
      return null
    case 'CaseManager':
      return features.AIAssistantCaseManager ? null : 'ROLE_NOT_ALLOWED'
    case 'Specialist':
      return features.AIAssistantSpecialist ? null : 'ROLE_NOT_ALLOWED'
    default:
      return 'ROLE_NOT_ALLOWED'
  }
}

/** What the user is told when the assistant is not available to them, by reason. */
export const ASSISTANT_UNAVAILABLE_MESSAGES: Record<
  AssistantUnavailableReason,
  string
> = {
  FEATURE_DISABLED: 'The AI Assistant is not available for your organisation.',
  ROLE_NOT_ALLOWED: 'The AI Assistant is not available for your role.',
}
